import React, { memo, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import CelebrateGeneric from 'fe-design-base/assets/illustrations/CelebrateGeneric';
import Box from 'fe-design-base/atoms/Box';
import Dialog from 'fe-design-base/organisms/Dialog';
import { useIsEmbedded } from 'hooks/useIsEmbedded';
import PropTypes from 'prop-types';

import { showAddEmployeeModal } from 'actions/addTeam';

import {
  selectSuccessModalIsOpen,
  selectSuccessModalProps,
} from 'features/team/selectors';
import { closeSuccessModal, openTaxEntryDrawer } from 'features/team/slice';
import { getContactInfo } from 'features/team/util';
import {
  BUTTON_TEXT,
  ELEMENT,
  EVENT_ACTIONS,
} from 'features/teamView/tracking';

import { cxHelpers } from 'util/className';
import {
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';
import { useTrackUx, withUxRoot } from 'util/uxEvents';

import ActionButtons from './components/ActionButtons';
import UserInfo from './components/UserInfo';
import AddEmployeeModalFooter from './AddEmployeeModalFooter';

const { cx } = cxHelpers('AddEmployeeSuccessModal');

export const AddEmployeeSuccessModal = ({
  isOpen,
  onShowAddEmployeeModal,
  onClose,
  onOpenTaxEntryDrawer,
  modalProps: {
    fullName,
    phone,
    email,
    merge_user_request_matched_on,
    invite_status,
    title,
    userId,
    isPayrollEnrolled,
    payrollOnboardingOptions,
    onCloseDrawerCallback,
    isPayrollSetupDone,
    onboardingOptions,
  },
}) => {
  const trackUx = useTrackUx();
  const isEmbedded = useIsEmbedded();

  useEffect(() => {
    if (isOpen) {
      if (isPayrollSetupDone) {
        trackUx(
          EVENT_ACTIONS.ADD_TEAM_PAYROLL_SUCCESS_MODAL_SHOWN,
          TRACK_ACTION_TYPES.VIEW,
          {
            eventCategory: EVENT_CATEGORIES.ADD_TEAM_PAYROLL_FLOW,
          }
        );
      } else {
        trackUx(EVENT_ACTIONS.MODAL_VIEWED, TRACK_ACTION_TYPES.VIEW, {
          eventCategory: EVENT_CATEGORIES.ADD_TEAM_SUCCESS,
          payroll_onboarding_type: isPayrollEnrolled
            ? payrollOnboardingOptions
            : null,
        });
      }
    }
  }, [
    isOpen,
    isPayrollEnrolled,
    payrollOnboardingOptions,
    isPayrollSetupDone,
    trackUx,
  ]);

  const handleClickUser = useCallback(() => {
    trackUx(EVENT_ACTIONS.LINK_CLICKED, TRACK_ACTION_TYPES.CLICK, {
      eventCategory: EVENT_CATEGORIES.ADD_TEAM_SUCCESS,
      element: ELEMENT.EMPLOYEE_NAME_IN_SUCCESS_MODAL,
    });
    onClose();
  }, [onClose, trackUx]);

  const handleAddAnotherTeamMember = useCallback(() => {
    trackUx(EVENT_ACTIONS.LINK_CLICKED, TRACK_ACTION_TYPES.CLICK, {
      eventCategory: EVENT_CATEGORIES.ADD_TEAM_SUCCESS,
      element: ELEMENT.ADD_ANOTHER_TEAM_MEMBER,
    });

    onClose();
    /** Wait for modal to close before reopening AddEmployeeForm,
     * to prevent blur error on firstName */
    setTimeout(() => {
      onShowAddEmployeeModal();
    }, 300);
  }, [trackUx, onClose, onShowAddEmployeeModal]);

  const handleClickDone = useCallback(() => {
    trackUx(EVENT_ACTIONS.BUTTON_CLICKED, TRACK_ACTION_TYPES.CLICK, {
      eventCategory: EVENT_CATEGORIES.ADD_TEAM_SUCCESS,
      button_text: BUTTON_TEXT.DONE,
    });
    if (onCloseDrawerCallback) onCloseDrawerCallback();
    onClose();
  }, [onClose, trackUx, onCloseDrawerCallback]);

  const handleSetupPayroll = useCallback(() => {
    trackUx(EVENT_ACTIONS.BUTTON_CLICKED, TRACK_ACTION_TYPES.CLICK, {
      eventCategory: EVENT_CATEGORIES.ADD_TEAM_PAYROLL_FLOW,
      button_text: BUTTON_TEXT.ENTER_TAX_DETAILS,
    });
    onClose();
    onOpenTaxEntryDrawer(userId);
  }, [onClose, onOpenTaxEntryDrawer, trackUx, userId]);

  const handleLinkClicked = useCallback(() => {
    trackUx(EVENT_ACTIONS.LINK_CLICKED, TRACK_ACTION_TYPES.CLICK, {
      eventCategory: EVENT_CATEGORIES.ADD_TEAM_PAYROLL_FLOW,
      button_text: BUTTON_TEXT.ILL_DO_THIS_LATER,
    });
    if (onCloseDrawerCallback) onCloseDrawerCallback();
    onClose();
  }, [onClose, onCloseDrawerCallback, trackUx]);

  const contact = getContactInfo(
    phone,
    email,
    fullName,
    merge_user_request_matched_on
  );

  return (
    <Dialog
      noPadding
      open={isOpen}
      width={420}
      fullScreen={isEmbedded}
      onClose={onClose}
    >
      {() => (
        <Box
          className={cx()}
          column
          mv={32}
          mh={isEmbedded ? 'auto' : 32}
          mt={isEmbedded ? '20vh' : 32}
          maxw={isEmbedded ? 600 : null}
        >
          <Box mt={24} mb={24}>
            <CelebrateGeneric h={164} w={164} m="auto" />
          </Box>

          <UserInfo
            title={title}
            userId={userId}
            fullName={fullName}
            onClickUser={handleClickUser}
          />

          <AddEmployeeModalFooter
            isPayrollEnrolled={isPayrollEnrolled}
            isPayrollSetupDone={isPayrollSetupDone}
            onboardingOptions={onboardingOptions}
            inviteStatus={invite_status}
            contact={contact}
          />

          <ActionButtons
            onClickDone={handleClickDone}
            onAddAnotherTeamMember={handleAddAnotherTeamMember}
            isPayrollEnrolled={isPayrollEnrolled}
            isPayrollSetupDone={isPayrollSetupDone}
            onLinkClicked={handleLinkClicked}
            onSetupPayroll={handleSetupPayroll}
          />
        </Box>
      )}
    </Dialog>
  );
};

AddEmployeeSuccessModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onOpenTaxEntryDrawer: PropTypes.func.isRequired,
  onShowAddEmployeeModal: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  modalProps: PropTypes.shape({
    fullName: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    merge_user_request_matched_on: PropTypes.string,
    invite_status: PropTypes.string,
    onboardingOptions: PropTypes.string,
    userId: PropTypes.number,
    title: PropTypes.string,
    isPayrollEnrolled: PropTypes.bool,
    payrollOnboardingOptions: PropTypes.string,
    onCloseDrawerCallback: PropTypes.func,
    isPayrollSetupDone: PropTypes.bool,
  }),
};

export default connect(
  state => ({
    modalProps: selectSuccessModalProps(state),
    isOpen: selectSuccessModalIsOpen(state),
  }),
  {
    onShowAddEmployeeModal: showAddEmployeeModal,
    onClose: closeSuccessModal,
    onOpenTaxEntryDrawer: openTaxEntryDrawer,
  }
)(
  withUxRoot({
    productArea: PRODUCT_AREAS.TEAM,
  })(memo(AddEmployeeSuccessModal))
);
